import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Sparkles, AlertTriangle, CalendarDays, ListTodo, MessageSquare, Lightbulb, Copy, Check, Users } from "lucide-react";

function Card({ icon: Icon, title, children, testId }) {
  return (
    <section className="rounded-2xl border border-border bg-card p-5" data-testid={testId}>
      <div className="mb-3 flex items-center gap-2"><Icon className="h-4 w-4 text-primary" /><p className="text-sm font-semibold text-foreground">{title}</p></div>
      {children}
    </section>
  );
}

function CopyBtn({ text }) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  return (
    <Button size="sm" variant="outline" className="gap-1.5 rounded-full" data-testid="aipa-brief-copy"
      onClick={async () => { try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* noop */ } }}>
      {copied ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />} {copied ? t("aipa.copied") : t("aipa.copy")}
    </Button>
  );
}

export default function Briefing() {
  const t = useT();
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gen, setGen] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/coach/assistant/briefing/today"); setBrief(data?.sections ? data : null); }
    catch { setBrief(null); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setGen(true);
    try { const { data } = await api.post("/coach/assistant/briefing/generate"); setBrief(data); }
    catch (err) { toast.error(apiError(err, t("aipa.checkout_error"))); } finally { setGen(false); }
  };

  if (loading) return <Skeleton className="h-64 rounded-2xl" />;
  const s = brief?.sections || {};

  return (
    <div className="space-y-4" data-testid="aipa-briefing">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl text-foreground">{t("aipa.briefing_title")}</h2>
        <Button className="gap-1.5 rounded-full" disabled={gen} onClick={generate} data-testid="aipa-brief-generate">
          <Sparkles className="h-4 w-4" /> {gen ? t("aipa.briefing_generating") : (brief ? t("aipa.briefing_regenerate") : t("aipa.briefing_generate"))}
        </Button>
      </div>
      {!brief ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center" data-testid="aipa-brief-empty">
          <Sparkles className="mx-auto h-8 w-8 text-primary" />
          <p className="mt-3 text-sm text-muted-foreground">{t("aipa.briefing_empty")}</p>
        </div>
      ) : (
        <>
          {s.clients_summary && <Card icon={Users} title={t("aipa.briefing_clients")} testId="aipa-brief-clients"><p className="text-sm text-foreground">{s.clients_summary}</p></Card>}
          {(s.attention_needed || []).length > 0 && (
            <Card icon={AlertTriangle} title={t("aipa.briefing_attention")} testId="aipa-brief-attention">
              <ul className="space-y-1.5">{s.attention_needed.map((a, i) => <li key={i} className="text-sm text-foreground">• {a}</li>)}</ul>
            </Card>
          )}
          {s.calendar_summary && <Card icon={CalendarDays} title={t("aipa.briefing_calendar")} testId="aipa-brief-calendar"><p className="text-sm text-foreground">{s.calendar_summary}</p></Card>}
          {s.tasks_summary && <Card icon={ListTodo} title={t("aipa.briefing_tasks")} testId="aipa-brief-tasks"><p className="text-sm text-foreground">{s.tasks_summary}</p></Card>}
          {(s.communication_suggestions || []).length > 0 && (
            <Card icon={MessageSquare} title={t("aipa.briefing_comm")} testId="aipa-brief-comm">
              <div className="space-y-3">
                {s.communication_suggestions.map((c, i) => (
                  <div key={i} className="rounded-xl border border-border bg-background p-3">
                    <p className="text-sm font-medium text-foreground">{c.client}</p>
                    {c.reason && <p className="text-xs text-muted-foreground">{c.reason}</p>}
                    {c.draft && <p className="mt-2 whitespace-pre-wrap rounded-lg bg-secondary/50 p-2 text-sm text-foreground">{c.draft}</p>}
                    {c.draft && <div className="mt-2 flex justify-end"><CopyBtn text={c.draft} /></div>}
                  </div>
                ))}
              </div>
            </Card>
          )}
          {(s.post_ideas || []).length > 0 && (
            <Card icon={Lightbulb} title={t("aipa.briefing_posts")} testId="aipa-brief-posts">
              <div className="space-y-2">
                {s.post_ideas.map((p, i) => (
                  <div key={i} className="rounded-xl border border-border bg-background p-3">
                    <p className="text-sm font-medium text-foreground">{p.title}</p>
                    {p.reason && <p className="text-xs text-muted-foreground">{p.reason}</p>}
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
